<template>
	<v-row class="full-height">
		<v-col cols="12" class="flex-none">
			<div class="payment-actions mb-6">
				<v-btn depressed outlined @click="handleCancel">Cancel</v-btn>
				<v-btn depressed outlined :disabled="isSaving" :loading="isSaving" @click="savePayments">Save</v-btn>
			</div>

			<div class="payment-form">
				<v-row>
					<v-col class="payment-form-header">
						<h1 class="payment-form-title">Payments</h1>
						<div class="payment-form-info">
							<dl>
								<dt>Order #</dt>
								<dd>{{ orderNumber }}</dd>
								<dt>Balance:</dt>
								<dd>{{ renderPrice(balance) }}</dd>
							</dl>
						</div>
					</v-col>
				</v-row>

				<v-row>
					<div class="payment-form-table">
						<div class="flex-table" role="table-head">
							<div class="flex-table-row" role="row-group">
								<div class="flex-table-cell" role="cell">Payment Method</div>
								<div class="flex-table-cell" role="cell">School/Organization</div>
								<div class="flex-table-cell" role="cell">Payment Date</div>
								<div class="flex-table-cell" role="cell">Amount</div>
								<div class="flex-table-cell" role="cell">Name</div>
								<div class="flex-table-cell" role="cell">Ref #</div>
								<div class="flex-table-cell" role="cell">&nbsp;</div>
							</div>
						</div>
						<div class="flex-table" role="table-body">
							<template v-for="(data, i) in paymentsData">
								<div :key="i" class="flex-table-row" :class="{ 'has-errors': hasRowErrors(i) }" role="row-group">
									<div class="flex-table-cell" role="cell">
										<div class="form-input">
											<v-select
												v-model="data.paymentMethodId"
												:items="paymentMethods"
												item-text="paymentMethodName"
												item-value="paymentMethodId"
												dense
												outlined
												hide-details="auto"
												:error-messages="getFieldError(i, 'paymentMethodId')"
												@input="clearValidationErrors(i)"
											></v-select>
										</div>
									</div>
									<div class="flex-table-cell" role="cell">
										<div class="form-input">
											<v-text-field
												outlined
												dense
												hide-details="auto"
												v-model="data.schoolOrOrganization"
											></v-text-field>
										</div>
									</div>
									<div class="flex-table-cell" role="cell">
										<div class="form-input">
											<v-menu
												v-model="dateMenus[i]"
												:close-on-content-click="false"
												transition="scale-transition"
												offset-y
												min-width="290px"
											>
												<template v-slot:activator="{ on, attrs }">
													<div v-on="on">
														<v-text-field
															:value="getDateDisplayValue(data)"
															@input="handleDateInput($event, i)"
															@blur="handleDateBlur(i)"
															@focus="handleDateFocus(i)"
															append-icon="mdi-calendar"
															@click:append="dateMenus[i] = true"
															outlined
															dense
															hide-details="auto"
															placeholder="MM/DD/YYYY"
															v-bind="attrs"
															:error-messages="getFieldError(i, 'paymentDate')"
														></v-text-field>
													</div>
												</template>
												<v-date-picker
													:value="getSafeDatePickerValue(data)"
													@input="handleCalendarSelect($event, i)"
												></v-date-picker>
											</v-menu>
										</div>
									</div>
									<div class="flex-table-cell" role="cell">
										<div class="form-input">
											<currency-text-field
												v-model="data.amount"
												:allow-negative="true"
												:error-messages="getFieldError(i, 'amount')"
												@input="clearValidationErrors(i)"
											/>
										</div>
									</div>
									<div class="flex-table-cell" role="cell">
										<div class="form-input">
											<v-text-field
												outlined
												dense
												hide-details="auto"
												v-model="data.name"
											></v-text-field>
										</div>
									</div>
									<div class="flex-table-cell" role="cell">
										<div class="form-input">
											<v-text-field
												outlined
												dense
												hide-details="auto"
												v-model="data.referenceNumber"
											></v-text-field>
										</div>
									</div>
									<div class="flex-table-cell" role="cell">
										<div class="payment-form-row-action">
											<v-btn icon @click="removePaymentRow(data)">
												<v-icon>mdi-close</v-icon>
											</v-btn>
										</div>
									</div>
								</div>
							</template>
						</div>

						<v-btn text class="mt-1" color="accent" @click="addPaymentRow()">
							<v-icon>mdi-plus</v-icon>
							Add A Payment
						</v-btn>
					</div>
				</v-row>
			</div>

			<div class="payment-pricing">
				<div class="form-input">
					<label>Order Total:</label>
					<v-text-field
						outlined
						dense
						hide-details="auto"
						:value="renderPrice(orderTotal)"
						readonly
					></v-text-field>
				</div>
				<div class="form-input">
					<label>Payments:</label>
					<v-text-field
						outlined
						dense
						hide-details="auto"
						:value="renderPrice(computedPayments)"
						readonly
					></v-text-field>
				</div>
				<div class="form-input">
					<label>Balance:</label>
					<v-text-field
						outlined
						dense
						hide-details="auto"
						:value="renderPrice(computedBalance)"
						readonly
					></v-text-field>
				</div>
			</div>
		</v-col>

		<!-- Unsaved changes navigation warning dialog -->
		<ConfirmNavigationDialog
			:visible="showUnsavedChangesDialog"
			:loading="savingBeforeLeave"
			@stay="handleNavigationStay"
			@leave="handleNavigationLeave"
			@save-and-leave="handleNavigationSaveAndLeave"
		/>
	</v-row>
</template>

<script>
import gql from "graphql-tag";
import { renderPrice } from "@/helpers/PricingCalculator";
import { isFutureDate, toDateOnly, todayDateOnly } from "@/helpers/paymentDate";
import UnsavedChangesGuard from "@/mixins/UnsavedChangesGuard";
import ConfirmNavigationDialog from "@/components/ConfirmNavigationDialog.vue";

export default {
	name: "SalesOrdersPayments",
	mixins: [UnsavedChangesGuard],
	components: {
		ConfirmNavigationDialog,
	},
	props: {
		orderNumber: {
			default: "",
		},
		orderTotal: {
			default: "",
		},
		balance: {
			default: "",
		},
		payments: {
			type: Array,
			default() {
				return [];
			},
		},
	},
	data() {
		return {
			paymentsData: [],
			showPaymentDateByRow: null,
			paymentMethods: [],
			validationErrors: {},
			dateMenus: {},
			isSaving: false,
		};
	},
	apollo: {
		paymentMethods: {
			query() {
				return gql`
					query {
						paymentMethods(take: 500) {
							items {
								paymentMethodId
								paymentMethodName
							}
						}
					}
				`;
			},
			update: (data) => data.paymentMethods.items,
		},
	},
	methods: {
		renderPrice,

		/**
		 * Returns the state that should be tracked for unsaved changes
		 * Required by UnsavedChangesGuard mixin
		 */
		getTrackableState() {
			return {
				paymentsData: this.paymentsData.map((payment) => ({
					quotePaymentId: payment.quotePaymentId,
					salesOrderPaymentId: payment.salesOrderPaymentId,
					paymentMethodId: payment.paymentMethodId,
					schoolOrOrganization: payment.schoolOrOrganization,
					paymentDate: payment.paymentDate,
					amount: payment.amount,
					name: payment.name,
					referenceNumber: payment.referenceNumber,
					checkNumber: payment.checkNumber, // Keep for backward compatibility
					notes: payment.notes, // Keep for backward compatibility
				})),
			};
		},

		async saveBeforeLeave() {
			this.savePayments();
		},

		/**
		 * Handle cancel button with unsaved changes check
		 */
		async handleCancel() {
			// Check for unsaved changes before canceling
			if (this.hasUnsavedChanges && this.showNavigationWarning) {
				const shouldProceed = await this.confirmNavigation();
				if (!shouldProceed) {
					return; // User chose to stay
				}
			}

			// Proceed with cancel
			this.$emit('cancel');
		},

		formatDate(date) {
			if (!date) return "";

			date = date.substring(0, 10);
			const [year, month, day] = date.split("-");
			return `${month}/${day}/${year}`;
		},

		/**
		 * Get the display value for the date field
		 * Returns _dateDisplay if editing, otherwise formatted paymentDate
		 */
		getDateDisplayValue(payment) {
			if (payment._dateDisplay !== undefined) {
				return payment._dateDisplay;
			}
			if (payment.paymentDate) {
				return this.formatDate(payment.paymentDate);
			}
			return "";
		},

		/**
		 * Check if the date value is valid for the v-date-picker
		 */
		isValidDateForPicker(dateValue) {
			if (!dateValue) return false;
			// Check if it's a valid ISO date format (YYYY-MM-DD)
			const isoPattern = /^\d{4}-\d{2}-\d{2}$/;
			if (!isoPattern.test(dateValue)) return false;

			// Check if it's actually a valid date
			const date = new Date(dateValue + 'T00:00:00');
			return date instanceof Date && !isNaN(date);
		},

		/**
		 * Get a safe date value for the v-date-picker
		 * Returns the payment date if valid, otherwise returns today's date
		 */
		getSafeDatePickerValue(payment) {
			const dateValue = payment.paymentDate;
			if (this.isValidDateForPicker(dateValue)) {
				return dateValue;
			}
			return todayDateOnly();
		},

		/**
		 * Handle focus on date field - preserve the value
		 */
		handleDateFocus(index) {
			const payment = this.paymentsData[index];
			// When user clicks into the field, set _dateDisplay to current formatted value
			// This prevents the value from disappearing
			if (payment._dateDisplay === undefined && payment.paymentDate) {
				this.$set(payment, '_dateDisplay', this.formatDate(payment.paymentDate));
			}
		},

		/**
		 * Handle calendar picker selection
		 */
		handleCalendarSelect(selectedDate, index) {
			const payment = this.paymentsData[index];
			// Update the payment date with selected value
			payment.paymentDate = selectedDate;
			// Clear the _dateDisplay so it shows the formatted paymentDate
			this.$delete(payment, '_dateDisplay');
			// Close the calendar menu
			this.$set(this.dateMenus, index, false);
			this.clearValidationErrors(index);
		},

		/**
		 * Handle manual date input with MM/DD/YYYY masking
		 * Applies masking as user types for better UX
		 */
		handleDateInput(value, index) {
			const payment = this.paymentsData[index];

			if (!value) {
				this.$set(payment, '_dateDisplay', '');
				return;
			}

			// Remove all non-numeric characters
			let cleaned = value.replace(/\D/g, "");

			// Limit to 8 digits (MMDDYYYY)
			cleaned = cleaned.substring(0, 8);

			// Apply MM/DD/YYYY mask as user types
			let masked = "";
			if (cleaned.length > 0) {
				masked = cleaned.substring(0, 2); // MM
			}
			if (cleaned.length >= 3) {
				masked += "/" + cleaned.substring(2, 4); // DD
			}
			if (cleaned.length >= 5) {
				masked += "/" + cleaned.substring(4, 8); // YYYY
			}

			// Update the display value with masking using Vue.set for reactivity
			this.$set(payment, '_dateDisplay', masked);
		},

		/**
		 * Handle date blur - parse and validate the manually entered date
		 */
		handleDateBlur(index) {
			const payment = this.paymentsData[index];
			const value = payment._dateDisplay || "";

			if (!value) {
				payment.paymentDate = "";
				this.$delete(payment, '_dateDisplay');
				this.clearValidationErrors(index);
				return;
			}

			// Remove any non-numeric characters and try to parse
			const cleaned = value.replace(/\D/g, "");

			// Try to parse various formats
			let month, day, year;

			// Check if already in MM/DD/YYYY format
			const slashPattern = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
			const slashMatch = value.match(slashPattern);

			if (slashMatch) {
				month = slashMatch[1].padStart(2, "0");
				day = slashMatch[2].padStart(2, "0");
				year = slashMatch[3];
			} else if (cleaned.length === 8) {
				// Parse MMDDYYYY format
				month = cleaned.substring(0, 2);
				day = cleaned.substring(2, 4);
				year = cleaned.substring(4, 8);
			} else if (cleaned.length === 6) {
				// Parse MMDDYY format (assume 20YY for years < 50, 19YY otherwise)
				month = cleaned.substring(0, 2);
				day = cleaned.substring(2, 4);
				const yearShort = parseInt(cleaned.substring(4, 6));
				year = yearShort < 50 ? `20${cleaned.substring(4, 6)}` : `19${cleaned.substring(4, 6)}`;
			} else {
				// Invalid format
				this.$toast?.warning("Please enter date in MM/DD/YYYY format.", {
					timeout: 3000,
				});
				// Reset to previous valid date or empty
				if (payment.paymentDate) {
					this.$set(payment, '_dateDisplay', this.formatDate(payment.paymentDate));
				} else {
					this.$set(payment, '_dateDisplay', '');
				}
				return;
			}

			// Validate the date is actually valid using local time (avoids timezone issues)
			const testDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
			const isValidDate =
				testDate instanceof Date &&
				!isNaN(testDate) &&
				testDate.getFullYear() === parseInt(year) &&
				testDate.getMonth() + 1 === parseInt(month) &&
				testDate.getDate() === parseInt(day);

			if (isValidDate) {
				// Create YYYY-MM-DD format for v-model
				const isoDate = `${year}-${month}-${day}`;
				payment.paymentDate = isoDate;
				// Clear _dateDisplay so it shows the formatted paymentDate
				this.$delete(payment, '_dateDisplay');
				this.clearValidationErrors(index);
			} else {
				// Invalid date (e.g., 02/31/2024 or 13/01/2024)
				this.$toast?.error("Invalid date. Please enter a valid date.", {
					timeout: 3000,
				});
				// Reset to previous valid date or clear
				this.$delete(payment, '_dateDisplay');
			}
		},

		addPaymentRow() {
			const newPayment = {
				paymentId: 0,
				paymentMethodId: null,
				schoolOrOrganization: "",
				paymentDate: todayDateOnly(),
				amount: null,
				name: "",
				referenceNumber: "",
			};
			this.paymentsData.push(newPayment);
		},

		removePaymentRow(rowData) {
			this.paymentsData = this.paymentsData.filter((obj) => obj !== rowData);
		},

		clearValidationErrors(rowIndex) {
			// Clear errors for a specific row when user starts editing
			if (this.validationErrors[rowIndex]) {
				this.$delete(this.validationErrors, rowIndex);
			}
		},

		hasRowErrors(rowIndex) {
			// Check if a specific row has any validation errors
			return this.validationErrors[rowIndex] && this.validationErrors[rowIndex].length > 0;
		},

		getFieldError(rowIndex, fieldName) {
			// Get error message for a specific field in a specific row
			if (!this.validationErrors[rowIndex]) {
				return [];
			}
			const errors = this.validationErrors[rowIndex];
			if (fieldName === "paymentMethodId" && errors.includes("Payment method is required")) {
				return ["Payment method is required"];
			}
			if (fieldName === "paymentDate" && errors.includes("Payment date cannot be in the future")) {
				return ["Date cannot be in the future"];
			}
			return [];
		},

		isEmptyPayment(payment) {
			// A payment is considered empty if ALL fields are empty/null
			return (
				!payment.paymentMethodId &&
				!payment.amount &&
				!payment.name &&
				!payment.referenceNumber &&
				!payment.schoolOrOrganization
			);
		},

		validatePayment(payment) {
			const errors = [];
			const amount = parseFloat(payment.amount) || 0;

			// Payment method required for any non-empty payment
			// (If user filled in ANY field, they need to select a payment method)
			if (!payment.paymentMethodId) {
				errors.push("Payment method is required");
			}

			// Payment date cannot be in the future. Compare calendar dates as text rather than
			// Date instants: parsing the server's "…T00:00:00.000Z" (or a bare "YYYY-MM-DD")
			// yields UTC midnight, which is "after" local midnight for any browser at or east
			// of UTC and wrongly rejected today's date (#1445). "Today" is read at validation
			// time, not cached, so a payments screen left open past midnight stays correct.
			if (isFutureDate(payment.paymentDate, todayDateOnly())) {
				errors.push("Payment date cannot be in the future");
			}

			return errors;
		},

		savePayments() {
			if (this.isSaving) return;
			this.isSaving = true;

			// Clear all previous validation errors
			this.validationErrors = {};

			// Filter out completely empty payments and validate non-empty ones
			const validPayments = [];
			let hasErrors = false;

			this.paymentsData.forEach((payment, index) => {
				// Skip completely empty payments
				if (this.isEmptyPayment(payment)) {
					return;
				}

				// Validate non-empty payments
				const errors = this.validatePayment(payment);
				if (errors.length > 0) {
					// Store errors for this row to display on the form
					this.$set(this.validationErrors, index, errors);
					hasErrors = true;
					return;
				}

				// Normalize payment data - default amount to 0.00 if not provided, and send the
				// date as YYYY-MM-DD so a loaded timestamp never round-trips a time component
				validPayments.push({
					...payment,
					paymentDate: toDateOnly(payment.paymentDate),
					amount: parseFloat(payment.amount) || 0.0,
				});
			});

			// If there are validation errors, don't proceed
			if (hasErrors) {
				this.isSaving = false;
				this.$toast?.error("Please fix the validation errors highlighted below", {
					timeout: 5000,
				});
				return;
			}

			// Disable navigation warning before emitting (user is explicitly saving)
			this.showNavigationWarning = false;

			// Emit validated and filtered payments to parent
			this.$emit("updatePayments", validPayments);

			// Clean up any temporary properties before resetting state
			this.paymentsData.forEach(payment => {
				if (payment._dateDisplay !== undefined) {
					this.$delete(payment, '_dateDisplay');
				}
			});

			// Reset initial state after successful save
			this.$nextTick(() => {
				this.resetInitialState();
			});
		},
	},
	computed: {
		computedPayments() {
			let paymentArr = [];
			this.paymentsData.forEach((obj) => {
				if (obj.amount !== null && obj.amount !== undefined) {
					const amount = parseFloat(obj.amount);
					if (!isNaN(amount)) {
						paymentArr.push(amount);
					}
				}
			});

			return paymentArr.reduce((a, b) => a + b, 0);
		},
		computedBalance() {
			// Ensure we never return NaN
			const orderTotal = parseFloat(this.orderTotal) || 0;
			const payments = parseFloat(this.computedPayments) || 0;
			const balance = parseFloat(this.balance) || 0;

			// If passed in balance is 0, return 0, otherwise calculate remaining balance
			if (balance === 0) {
				return 0;
			}

			const calculatedBalance = orderTotal - payments;
			return isNaN(calculatedBalance) ? 0 : calculatedBalance;
		},
	},
	watch: {
		payments: {
			handler(newPayments) {
				// Refresh paymentsData when payments prop changes
				if (newPayments && newPayments.length > 0) {
					this.paymentsData = JSON.parse(JSON.stringify(newPayments));
				}
			},
			deep: true,
		},
	},

	mounted() {
		if (this.payments.length > 0) {
			//strip reactivity so payment changes dont affect passed in payments without saving
			this.paymentsData = JSON.parse(JSON.stringify(this.payments));
		} else {
			this.addPaymentRow();
		}

		// Capture initial state after payments data is set up
		this.$nextTick(() => {
			this.captureInitialState();
		});
	},
};
</script>

<style lang="scss" scoped>
.payment {
	&-actions {
		display: flex;
		width: 100%;
		justify-content: flex-end;

		.v-btn {
			background-color: $color-white;
			border-color: $color-gray;
			text-transform: initial;

			&:not(:last-child) {
				margin-right: 0.8rem;
			}
		}
	}

	&-form {
		position: relative;
		display: block;
		background-color: $color-white;
		border: 1px solid $color-gray;
		border-radius: 5px;
		padding: 0.5rem 1.5rem;

		.row {
			margin: initial;
		}

		&-header {
			display: flex;
			align-items: center;

			a {
				color: $color-tertiary;
			}
		}

		&-info {
			margin-left: auto;

			dl {
				display: flex;
				flex-flow: row wrap;

				dt {
					margin-left: 3rem;
				}

				dd {
					margin-left: 1rem;
				}
			}
		}

		&-table {
			position: relative;
			width: 100%;

			.flex-table {
				border: none;

				//table-head
				&:first-of-type {
					.flex-table-row {
						min-height: 6rem;
					}
					.flex-table-cell {
						padding: 0rem 0.8rem;
					}
				}

				//table-body
				&:last-of-type {
					.flex-table-row {
						border: none;
						border-bottom: 1px solid $color-gray;

						// Only add bottom padding when row has validation errors
						&.has-errors {
							padding-bottom: 1.5rem;
						}
					}
				}

				.flex-table-cell {
					&:nth-child(1) {
						width: 18%;
					}
					&:nth-child(2) {
						width: 18%;
					}
					&:nth-child(3) {
						width: 13%;
					}
					&:nth-child(4) {
						width: 11%;
					}
					&:nth-child(5) {
						width: 17%;
					}
					&:nth-child(6) {
						width: 17%;
					}
					&:nth-child(7) {
						width: 6%;
						justify-content: center;
						align-items: center;
					}
				}

				.form-input {
					width: 100%;
					margin: initial;
				}
			}
		}
	}

	&-pricing {
		display: inline-flex;
		flex-flow: column nowrap;
		width: 100%;
		text-align: right;
		align-items: flex-end;
		margin-top: 2rem;

		.form-input {
			flex-flow: row;
			align-items: center;
			margin: 0.4rem 0;

			label {
				font-weight: 700;
				margin-right: 1rem;
			}
			.v-text-field {
				min-width: 16rem;
				max-width: 16rem;
				text-align: right;

				&::v-deep {
					input {
						text-align: right;
						padding-right: 0.8rem;
					}
				}
			}
		}
	}
}
</style>
